import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { startOfMonth } from 'date-fns';
import { useMemo } from 'react';
import { useLinearLocale } from '../../locale/LocaleContext.mjs';
import { YEAR_MONTH_DATE_FORMAT_RECORD } from '../constants/date-format.mjs';
import { useDatePickerContext } from '../context/datePickerContext.mjs';
import { useDatePickerField } from '../hooks/useDatePickerField.mjs';
import useEffectOnce from '../hooks/useEffectOnce.mjs';
import { isBeforeYearMonth, isAfterYearMonth, isSameYearMonth } from '../utils/date.mjs';
import { isDayRangeCollapsed } from '../utils/range.mjs';
import TextFieldWithErrorTooltip from './base/TextFieldWithErrorTooltip.mjs';
import { localeValues } from './localeValues.mjs';

const DATE_PICKER_MONTH_INPUTS_NAME = "DatePickerMonthInput";
const DatePickerMonthInput = ({
  __scopeDatePicker,
  ...props
}) => {
  const { autoFocus, style, ...rest } = props;
  const {
    mode,
    selectedDate,
    onDateChange,
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    excludeDates = [],
    minDate,
    maxDate
  } = useDatePickerContext(DATE_PICKER_MONTH_INPUTS_NAME, __scopeDatePicker);
  const { locale } = useLinearLocale();
  const isRangeMode = mode === "range";
  const {
    notAllowedMonthErrorMessage,
    invalidRangeInputErrorMessage,
    startYearMonthPlaceholder,
    endYearMonthPlaceholder,
    yearMonthPlaceholder
  } = localeValues[locale];
  const validateExcludeDates = (date) => {
    if (excludeDates.some((excludeDate) => isSameYearMonth(date, excludeDate))) {
      return [false, notAllowedMonthErrorMessage];
    }
    return [true, void 0];
  };
  const memoizedSelectedDate = useMemo(() => {
    return selectedDate !== null ? startOfMonth(selectedDate) : null;
  }, [selectedDate]);
  const memoizedStartDate = useMemo(() => {
    return startDate !== null ? startOfMonth(startDate) : null;
  }, [startDate]);
  const memoizedEndDate = useMemo(() => {
    return endDate !== null ? startOfMonth(endDate) : null;
  }, [endDate]);
  const [
    selectedDateString,
    selectedDateFieldProps,
    selectedDateFieldState,
    onApplyCurrentSelectedDateFieldValue
  ] = useDatePickerField({
    locale,
    date: memoizedSelectedDate,
    dateFormatRecord: YEAR_MONTH_DATE_FORMAT_RECORD,
    onDateChange,
    onBlur: props.onBlur,
    onChange: "onChange" in props ? props.onChange : void 0,
    onKeyDown: props.onKeyDown,
    onValidate: (date) => {
      if (!isNil(minDate) && isBeforeYearMonth(date, minDate) || !isNil(maxDate) && isAfterYearMonth(date, maxDate)) {
        return [false, notAllowedMonthErrorMessage];
      }
      return validateExcludeDates(date);
    },
    inputType: "month"
  });
  const [
    startDateString,
    startDateFieldProps,
    startDateFieldState,
    onApplyCurrentStartDateFieldValue
  ] = useDatePickerField({
    locale,
    date: memoizedStartDate,
    dateFormatRecord: YEAR_MONTH_DATE_FORMAT_RECORD,
    onDateChange: onStartDateChange,
    onBlur: props.onBlur,
    onChange: "onStartDateInputChange" in props ? props.onStartDateInputChange : void 0,
    onKeyDown: props.onKeyDown,
    onValidate: (date) => {
      if (!isNil(minDate) && isBeforeYearMonth(date, minDate)) {
        return [false, notAllowedMonthErrorMessage];
      }
      if (endDate !== null && isDayRangeCollapsed(date, endDate)) {
        return [false, invalidRangeInputErrorMessage];
      }
      return validateExcludeDates(date);
    },
    inputType: "month"
  });
  const [
    endDateString,
    endDateFieldProps,
    endDateFieldState,
    onApplyCurrentEndDateFieldValue
  ] = useDatePickerField({
    locale,
    date: memoizedEndDate,
    dateFormatRecord: YEAR_MONTH_DATE_FORMAT_RECORD,
    onDateChange: onEndDateChange,
    onBlur: props.onBlur,
    onChange: "onEndDateInputChange" in props ? props.onEndDateInputChange : void 0,
    onKeyDown: props.onKeyDown,
    onValidate: (date) => {
      if (!isNil(maxDate) && isAfterYearMonth(date, maxDate)) {
        return [false, notAllowedMonthErrorMessage];
      }
      if (startDate !== null && isDayRangeCollapsed(startDate, date)) {
        return [false, invalidRangeInputErrorMessage];
      }
      return validateExcludeDates(date);
    },
    inputType: "month"
  });
  useEffectOnce(() => {
    if (mode === "single") {
      onApplyCurrentSelectedDateFieldValue();
    }
    if (mode === "range") {
      onApplyCurrentStartDateFieldValue();
      onApplyCurrentEndDateFieldValue();
    }
  });
  return /* @__PURE__ */ jsx(
    TextFieldWithErrorTooltip.Wrapper,
    {
      style: {
        ...style,
        gap: isRangeMode ? 6 : 0
      },
      ...rest,
      children: isRangeMode ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          TextFieldWithErrorTooltip,
          {
            value: startDateString,
            errorTooltip: startDateFieldState.errorMessage,
            autoFocus,
            placeholder: "startPlaceholder" in props ? props.startPlaceholder : startYearMonthPlaceholder,
            ...startDateFieldProps
          }
        ),
        /* @__PURE__ */ jsx(
          TextFieldWithErrorTooltip,
          {
            value: endDateString,
            errorTooltip: endDateFieldState.errorMessage,
            placeholder: "endPlaceholder" in props ? props.endPlaceholder : endYearMonthPlaceholder,
            ...endDateFieldProps
          }
        )
      ] }) : /* @__PURE__ */ jsx(
        TextFieldWithErrorTooltip,
        {
          value: selectedDateString,
          errorTooltip: selectedDateFieldState.errorMessage,
          autoFocus,
          placeholder: props.placeholder ?? yearMonthPlaceholder,
          ...selectedDateFieldProps
        }
      )
    }
  );
};

export { DatePickerMonthInput as default };
//# sourceMappingURL=MonthInput.mjs.map
